import { type Article } from "./types";
import { isVideoUrl } from "./utils";

const COUNTRY_TO_ISO: Record<string, string> = {
  'south africa': 'za',
  'nigeria': 'ng',
  'zimbabwe': 'zw',
  'kenya': 'ke',
  'ghana': 'gh',
  'egypt': 'eg',
  'united states': 'us',
  'global': 'gl',
  'africa': 'af',
  'za': 'za',
  'ng': 'ng',
  'zw': 'zw',
  'ke': 'ke',
  'gh': 'gh',
  'eg': 'eg',
  'us': 'us',
  'gl': 'gl',
  'af': 'af'
};

export const FALLBACK_IMAGE = "https://firebasestorage.googleapis.com/v0/b/news-afrika.firebasestorage.app/o/Backup%20Images%2FrgjeV.jpg?alt=media&token=0e786671-8e57-42+f5-9f10-c28cc1c84148";

/**
 * Normalizes a document from Firestore into a consistent Article type.
 * Handles native Firestore Timestamps with millisecond precision.
 */
export function docToArticle(data: any): Article {
  if (!data) return {} as Article;
  
  const id = data.id || data.slug || Math.random().toString(36).substring(7);

  const parseDateField = (field: any) => {
    if (field && typeof field === 'object' && 'seconds' in field) {
      return new Date(field.seconds * 1000).toISOString();
    } else if (field) {
      const d = new Date(field);
      return isNaN(d.getTime()) ? new Date().toISOString() : d.toISOString();
    }
    return null;
  };

  const pubDate = parseDateField(data.publishDate) || parseDateField(data.timestamp) || new Date().toISOString();
  const upDate = data.updatedAt ? (parseDateField(data.updatedAt) || pubDate) : pubDate;

  const headline = data.headline || data.title || 'Untitled Article';

  let normalizedImages: any[] = [];
  if (Array.isArray(data.images)) {
    normalizedImages = data.images.filter((img: any) => img && typeof img.src === 'string' && img.src.trim() !== '');
  }

  if (normalizedImages.length === 0 && data.imageUrl && typeof data.imageUrl === 'string' && data.imageUrl.trim() !== '') {
    normalizedImages = [{ src: data.imageUrl, alt: headline }];
  }

  const primaryImageIsVideo = normalizedImages[0]?.src && isVideoUrl(normalizedImages[0].src);

  if (normalizedImages.length === 0) {
    normalizedImages = [{ src: FALLBACK_IMAGE, alt: headline }];
  }

  const subcategory = (data.subcategory || '').trim();

  const video = typeof data.video === 'object' ? { ...data.video } : {};
  if (data.asrc) {
    video.contentUrl = data.asrc;
  }

  // Ensure primary video source is extracted correctly
  if (primaryImageIsVideo && !video.contentUrl && !video.embedUrl) {
    video.contentUrl = normalizedImages[0].src;
  }

  // CEO FIX: Prevent broken/expensive thumbnails by ensuring poster is a STATIC IMAGE
  if (video.contentUrl || video.embedUrl) {
    const staticImage = normalizedImages.find(img => img.src && !isVideoUrl(img.src));
    video.thumbnail = video.thumbnail || staticImage?.src || FALLBACK_IMAGE;
  }

  // SEMANTIC PROTECTION: Detect platform URLs and strictly categorize as embed
  const firstMediaUrl = video.contentUrl || video.embedUrl || normalizedImages[0]?.src || '';
  const isSocial = firstMediaUrl.includes('x.com') || firstMediaUrl.includes('twitter.com') || firstMediaUrl.includes('youtube.com') || firstMediaUrl.includes('youtu.be');

  // Resolved type logic matching user request: social links = embed, direct files = video
  const resolvedType = (data.type === 'video' || data.type === 'embed' || !!data.asrc || primaryImageIsVideo || isSocial) 
    ? (isSocial ? 'embed' : 'video') 
    : 'article';

  const baseArticle: Article = {
    id: id,
    slug: data.slug || id, 
    headline: headline,
    subheadline: data.subheadline || data.excerpt || data.lede || '',
    lede: data.lede || data.subheadline || data.excerpt || '',
    author: data.author || 'News Afrika Newsroom',
    authorId: data.authorId || 'newsroom',
    authorProfileSlug: data.authorProfileSlug || data.authorId || 'newsroom',
    primaryCategory: data.primaryCategory || data.category || 'General',
    subcategory: subcategory,
    format: data.format || '',
    article: data.article || '', // Pass through the Sponsored tag
    type: resolvedType as any,
    region: data.region || 'Africa',
    country: data.country || 'ZA',
    publishDate: pubDate,
    updatedAt: upDate,
    status: data.status !== undefined ? data.status : true,
    pinned: !!data.pinned,
    event: data.event || '',
    tags: Array.isArray(data.tags) ? data.tags : [],
    highlights: Array.isArray(data.highlights) ? data.highlights : [],
    keywords: Array.isArray(data.keywords) ? data.keywords : [],
    images: normalizedImages,
    sources: Array.isArray(data.sources) ? data.sources : [],
    body: Array.isArray(data.body) ? data.body : (data.content ? [{type: 'paragraph', text: data.content}] : []),
    seoTitle: data.seoTitle || data.headline || data.title || '',
    seoDescription: data.seoDescription || data.subheadline || data.lede || '',
    video: Object.keys(video).length > 0 ? video : data.video || undefined,
  };

  return baseArticle;
}

/**
 * STRICT CANONICAL URL GENERATOR
 * Pattern: /[category]/[country]/[slug]
 */
export function getArticleUrl(article: Article): string {
  if (!article) return '/';
  
  // 1. Normalize Category
  const category = (article.primaryCategory || article.category || 'news')
    .toLowerCase()
    .trim()
    .replace(/&/g, 'and')
    .replace(/\s+/g, '-');
  
  // 2. Normalize Country ISO Code
  const rawCountry = (article.country || 'za').toLowerCase().trim();
  const countryCode = COUNTRY_TO_ISO[rawCountry] || (rawCountry.length === 2 ? rawCountry : 'za');
  
  // 3. Normalize Slug
  const slug = (article.slug || article.id || '').toLowerCase().trim();
  
  if (!slug) return `/${category}`;
  
  return `/${category}/${countryCode}/${slug}`;
}

export function getCategoryUrl(category: string, subcategory?: string, country?: string): string {
  if (!category || category.toLowerCase() === 'all') return '/';
  const slugify = (text: string) => text.toLowerCase().trim().replace(/&/g, 'and').replace(/\s+/g, '-').replace(/[^\w-]/g, '');
  const cat = slugify(category);
  if (country) {
    const iso = (COUNTRY_TO_ISO[country.toLowerCase()] || country.toLowerCase()).toLowerCase();
    return `/${cat}/${iso}`;
  }
  if (subcategory) {
    const sub = slugify(subcategory);
    return `/${cat}/${sub}`;
  }
  return `/${cat}`;
}
